'use client';
import HeaderCenter from '@components/shared/Header/HeaderCenter';
import { GridColFullWidth } from '@constants/layout';
import { TemplatesType } from '@lib/sanity/queries/modules/templates/types';
import { useClientConfig } from '@providers/ClientConfigProvider';
import Container from '@ui/Container/Container';
import { DESIGN_SIZE } from '@ui/Design/constants';
import DesignClient from '@ui/Design/DesignClient';
import Slider from '@ui/Slider/Slider';
import GradientOverlay from '@ui/StaggeredSlider/GradientOverlay';
import cn from '@utils/cn';
import { Template } from '../TemplatesModule';
import { getTemplateGenerateDesignUrl } from '../utils/urls';

type TemplatesMasonryProps = {
  data: TemplatesType;
  templates?: Template[];
};

export default function TemplatesMasonry({ data, templates }: TemplatesMasonryProps) {
  if (!templates || templates.length === 0) {
    return null;
  }

  function renderRow(row: Template[], i: number) {
    return <MasonryRow scrollSpeed={data.scrollSpeed ?? 80} index={i} key={i} templates={row} isOdd={i % 2 === 1} />;
  }

  // widen-amt is the amount of extra space to add to the sides of the container
  // in this case the whole page
  // otherwise wrap it in min(x, 300px) to avoid the module being too wide
  const style = {
    '--widen-amt': 'calc((100vw - 100%) / 2 + 10px)',
  } as React.CSSProperties;

  return (
    <Container theme={data.theme} className="bg-background" style={style}>
      <div className={cn('relative flex max-w-7xl flex-col gap-8 md:gap-16', GridColFullWidth)}>
        <HeaderCenter {...data} />
        <div className="relative -mx-[var(--widen-amt)] flex flex-col gap-8 overflow-hidden">
          <GradientOverlay className="w-32 md:w-64" isVisible isLeft={true} />
          {getRows(templates, data.nrOfRows ?? 2).map(renderRow)}
          <GradientOverlay className="w-32 md:w-64" isVisible isLeft={false} />
        </div>
      </div>
    </Container>
  );
}

function getRows(templates: Template[], rowsCount: number) {
  const rows: Template[][] = [];
  const templatesPerRow = Math.ceil(templates.length / rowsCount);

  for (let i = 0; i < rowsCount; i++) {
    const start = i * templatesPerRow;
    const end = start + templatesPerRow;
    rows.push(templates.slice(start, end));
  }

  return rows;
}

type MasonryRowProps = {
  templates: Template[];
  isOdd: boolean;
  scrollSpeed: number;
  index: number;
};

function MasonryRow({ templates, isOdd, scrollSpeed, index }: MasonryRowProps) {
  const { APP_URL } = useClientConfig();

  function renderTemplate(template: Template, i: number) {
    return (
      <DesignClient
        key={`${template.hash}_${i}_${index}`}
        url={getTemplateGenerateDesignUrl(template.hash, APP_URL)}
        className="max-w-[13rem] rounded-none border-none sm:max-w-[20.5rem] md:max-w-[28rem] lg:max-w-[28rem]"
        hash={template.hash}
        isAnimated={template.isAnimated ?? false}
        designSize={DESIGN_SIZE.SLIDER}
        hideIcon
        width={template.width ?? undefined}
        height={template.height ?? undefined}
      />
    );
  }

  return (
    <Slider
      items={templates.map(renderTemplate)}
      speed={scrollSpeed ?? 80}
      containerClassName="group"
      itemsClassName="group-hover:paused"
      isReversed={isOdd}
    />
  );
}
